import api from './api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'packages/logger';
import utils from './utils';
import {
    selectShipment,
    selectItemWithoutShipment,
    selectShippingPayment,
    selectCheckoutSummary,
    selectShippingCheckout,
    selectSellerPhoneNumber
} from './selector';
import {
    resolveMostPreciseTranslationKey,
    getDefaultErrorKey,
    isUnSupportedProduct
} from 'tradera/helpers/shipping-validation-errors';
import {
    setCheckoutSummaryFetching,
    openPaymentModal,
    closePaymentModal,
    setPaymentMethodData,
    resetOngoingPaymentAttempt
} from './reducer';
import { CHECKOUT_ERRORS } from 'tradera/helpers/checkout-errors';
import {
    MODAL_TYPE,
    MODAL_STATUS,
    PAYMENT_OPTIONS
} from 'tradera/state/views/shipping-checkout/initial-state';
import { paymentInstance } from 'tradera/apps/my-tradera/shipping-checkout/payment/braintree-drop-in';
import {
    selectMemberCountryCodeIso2,
    selectMemberEmail,
    selectMemberFirstName,
    selectMemberLastName
} from 'tradera/state/member/selectors';
import { NIL as NIL_UUID } from 'uuid';

export const deleteShipment = createAsyncThunk(
    'shippingCheckout/deleteShipment',
    async ({ shipmentId }, thunkAPI) => {
        try {
            const result = await api.deleteShipment(shipmentId);
            const { shipmentCheckoutId } = result?.data;
            return {
                shipmentId,
                shipmentCheckoutId
            };
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const createShipmentCheckout = createAsyncThunk(
    'shippingCheckout/createShipmentCheckout',
    async (callback = () => {}, thunkAPI) => {
        try {
            const result = await api.createShipmentCheckout();
            callback();

            return result.data;
        } catch (error) {
            logger(error);
            callback();

            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getShippingOptions = createAsyncThunk(
    'shippingCheckout/getShippingOptions',
    async (
        {
            fromCountry,
            toCountry,
            bringEnabled,
            postnordEnabled,
            dhlEnabled,
            schenkerEnabled,
            purchaseOrderId
        },
        thunkAPI
    ) => {
        try {
            const result = await api.getShippingOptions(
                fromCountry,
                toCountry,
                purchaseOrderId
            );

            return {
                data: result.data,
                bringEnabled: bringEnabled,
                postnordEnabled: postnordEnabled,
                dhlEnabled: dhlEnabled,
                schenkerEnabled: schenkerEnabled
            };
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const updateShipment = createAsyncThunk(
    'shippingCheckout/updateShipment',
    async (shipmentDetails, thunkAPI) => {
        thunkAPI.dispatch(setCheckoutSummaryFetching(true));

        let mostPreciseTranslationKey;
        try {
            const { data } = await api.setShipmentDetails(shipmentDetails);
            const { errors } = data.responseStatus;
            if (!errors) {
                if (data.shipmentCheckoutId !== NIL_UUID) {
                    return data;
                }

                return thunkAPI.dispatch(
                    createShipmentCheckout(() =>
                        thunkAPI.dispatch(setCheckoutSummaryFetching(false))
                    )
                );
            }

            mostPreciseTranslationKey =
                resolveMostPreciseTranslationKey(errors);
        } catch (error) {
            logger(error);
            mostPreciseTranslationKey = getDefaultErrorKey();
        }

        thunkAPI.dispatch(setCheckoutSummaryFetching(false));
        return thunkAPI.rejectWithValue({
            mostPreciseErrorKey: mostPreciseTranslationKey
        });
    }
);

export const updateShippingProvider = (id, payload) => (dispatch, getState) => {
    const shipment = selectShipment(getState(), id);

    const { providerId, id: productId } = payload;
    const { purchaseOrderId, shipmentId, receiver } = shipment;

    const shippingPayload = buildShippingPayload(
        shipmentId,
        providerId,
        productId,
        purchaseOrderId,
        receiver
    );

    dispatch(updateShipment(shippingPayload));
};

export const updateShippingAddress = (id, payload) => (dispatch, getState) => {
    const shipment = selectShipment(getState(), id);

    const { purchaseOrderId, shippingProvider, shipmentId, product, receiver } =
        shipment;

    const address = {
        name: payload.name || receiver.name,
        city: payload.city || receiver.city,
        zipCode: payload.zipCode || receiver.zipCode,
        street: payload.streetName || receiver.street
    };

    const shippingPayload = buildShippingPayload(
        shipmentId,
        shippingProvider,
        product.id,
        purchaseOrderId,
        address
    );
    dispatch(updateShipment(shippingPayload));
};

export const createShipment = createAsyncThunk(
    'shippingCheckout/createShipment',
    async (payload, thunkAPI) => {
        const {
            item: { selectedShipping },
            buyerContactDetails
        } = selectItemWithoutShipment(thunkAPI.getState());

        const { address } = payload;
        const shippingPayload = buildShippingPayload(
            null,
            payload.providerId || selectedShipping.providerId,
            payload.productId || selectedShipping.productId,
            payload.purchaseOrderId,
            {
                name: address?.name || buyerContactDetails.fullName,
                city: address?.city || buyerContactDetails.town,
                zipCode: address?.zipCode || buyerContactDetails.zipCode,
                street: address?.street || buyerContactDetails.streetName
            }
        );

        try {
            const { data } = await api.setShipmentDetails(shippingPayload);
            const { errors } = data.responseStatus;

            if (!errors) {
                return data;
            }

            // Can happen i combined shipping when seller sets custom shipping price and changes product
            // in that case we want to let the seller choose a product from shipping selector
            if (isUnSupportedProduct(errors[0])) {
                return thunkAPI.rejectWithValue({
                    hasUnSupportedProduct: true
                });
            }

            const mostPreciseTranslationKey =
                resolveMostPreciseTranslationKey(errors);

            return thunkAPI.rejectWithValue({
                mostPreciseErrorKey: mostPreciseTranslationKey
            });
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue({
                mostPreciseErrorKey: getDefaultErrorKey()
            });
        }
    }
);

export const markAsBooked = createAsyncThunk(
    'shippingCheckout/markAsBooked',
    async (purchaseOrderId, thunkAPI) => {
        try {
            return await api.setShippingAsBooked(purchaseOrderId);
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const markAsShipped = createAsyncThunk(
    'shippingCheckout/markAsShipped',
    async (purchaseOrderId, thunkAPI) => {
        try {
            return await api.setShippingAsShipped(purchaseOrderId);
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getItemWithoutShipment = createAsyncThunk(
    'shippingCheckout/getItemWithoutShipment',
    async (purchaseOrderId, thunkAPI) => {
        try {
            const { data } = await api.getMemberSoldItemShippingInfo(
                purchaseOrderId
            );
            const { responseStatus } = data;
            if (responseStatus?.errorCode) {
                return thunkAPI.rejectWithValue({
                    errorCode: responseStatus?.errorCode
                });
            }

            return data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getShippingConfirmation = createAsyncThunk(
    'shippingCheckout/getShippingConfirmation',
    async (shippingCheckoutId, thunkAPI) => {
        try {
            const { data } = await api.getShippingConfirmation(
                shippingCheckoutId
            );

            if (!data.pollBookingUrl) return data;

            const isBookingComplete = await pollShippingConfirmationStatus(
                data.pollBookingUrl,
                shippingCheckoutId
            );

            if (!isBookingComplete) {
                logger(
                    `Booking was not completed after 10 retries. shippingCheckoutId ${shippingCheckoutId}`
                );
                return thunkAPI.rejectWithValue({ isLongPolling: true });
            }

            const response = await api.getShippingConfirmation(
                shippingCheckoutId
            );
            return response.data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const pollShippingConfirmationStatus = async (pollBookingUrl) =>
    new Promise((resolve, reject) => {
        let iterations = 0;
        const poller = setInterval(async () => {
            if (iterations === 10) {
                clearInterval(poller);
                return resolve(false);
            }

            try {
                const { data } = await api.getShippingConfirmationStatus(
                    pollBookingUrl
                );

                if (data.isBookingComplete) {
                    clearInterval(poller);
                    return resolve(true);
                }
            } catch (error) {
                clearInterval(poller);
                logger(error);

                return reject(error);
            }
            // eslint-disable-next-line better-mutation/no-mutation
            iterations++;
        }, 2000);
    });

const buildShippingPayload = (
    shipmentId,
    shippingProvider,
    shippingProductId,
    purchaseOrderId,
    receiver
) => ({
    shipmentId, // nullable if `null` -> new shipment is created
    shippingProvider,
    buyerAddress: {
        name: receiver.name,
        city: receiver.city,
        zipCode: receiver.zipCode,
        streetName: receiver.street
    },
    shippingProductId,
    purchaseOrderId
});

// Payment

export const getPaymentOptions = createAsyncThunk(
    'shippingCheckout/getPaymentOptions',
    async (shipmentCheckoutId, thunkAPI) => {
        try {
            const { data } = await api.getPaymentOptions(shipmentCheckoutId);
            return data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const PAYMENT_STATUS = {
    AUTHORIZED: 'Authorized',
    AWAITING_SWISH: 'AwaitingExternalAuthentication',
    CANCELLED: 'Cancelled',
    CANCELLING: 'Cancelling',
    CANCELLING_FAILED: 'CancellingFailed',
    COMPLETING: 'Completing',
    COMPLETING_FAILED: 'CompletingFailed',
    DECLINED: 'Declined',
    FAILED: 'Failed',
    INIT: 'Initializing',
    INIT_COMPLETE: 'Initialized',
    INIT_FAILED: 'InitializationFailed',
    SETTLED: 'Settled'
};

// eslint-disable-next-line no-control-regex
const containsNonPrintableAscii = (value) => value.match(/[^\x00-\x7F]/g);

const createThreeDSecureParameters = (getState) => {
    const currentState = getState();
    const { totalCost } = selectCheckoutSummary(currentState);
    const memberFirstName = selectMemberFirstName(currentState);
    const firstName = containsNonPrintableAscii(memberFirstName)
        ? null
        : memberFirstName;
    const memberLastName = selectMemberLastName(currentState);
    const lastName = containsNonPrintableAscii(memberLastName)
        ? null
        : memberLastName;

    const buyerAddress = {
        phoneNumber: selectSellerPhoneNumber(currentState)?.replace(
            /[^\d]/g,
            ''
        ),
        countryCodeAlpha2: selectMemberCountryCodeIso2(currentState)
    };
    return {
        amount: totalCost,
        email: selectMemberEmail(currentState),
        billingAddress: {
            givenName: firstName,
            surname: lastName,
            ...buyerAddress
        },
        additionalInformation: {
            shippingGivenName: firstName,
            shippingSurname: lastName,
            shippingAddress: buyerAddress
        }
    };
};

export const initBraintreePayment =
    (paymentOption, onComplete) => async (dispatch, getState) => {
        let requestOptions = {};

        if (
            paymentOption === PAYMENT_OPTIONS.VISA_MASTERCARD ||
            paymentOption === PAYMENT_OPTIONS.GOOGLEPAY
        ) {
            requestOptions = {
                threeDSecure: createThreeDSecureParameters(getState)
            };
        }

        dispatch(setCheckoutSummaryFetching(true));

        try {
            if (paymentInstance) {
                const data = await paymentInstance.requestPaymentMethod(
                    requestOptions
                );
                dispatch(setPaymentMethodData(data));
            } else {
                throw { message: CHECKOUT_ERRORS.NO_PAYMENT_METHOD };
            }
            return dispatch(initPayment(onComplete));
        } catch (error) {
            return Promise.reject(error);
        }
    };

export const initPayment = (onComplete) => async (dispatch, getState) => {
    const currentState = getState();

    const { ongoingPaymentAttempt } = selectShippingCheckout(currentState);
    const payment = selectShippingPayment(currentState);
    const { shipmentCheckoutId, selectedPayment, paymentMethodData } = payment;

    dispatch(closePaymentModal());
    dispatch(setCheckoutSummaryFetching(true));

    try {
        const { shipmentPaymentAttemptId } = ongoingPaymentAttempt;
        const paymentAttemptId = shipmentPaymentAttemptId || utils.getUuid();

        if (!shipmentPaymentAttemptId) {
            const formData = {
                shipmentPaymentAttemptId: paymentAttemptId,
                shipmentCheckoutId,
                paymentMethod: selectedPayment,
                paymentMethodData
            };
            await api.initializePayment(formData);
        }

        //POST successful? then start polling:
        return dispatch(
            checkPaymentStatus(paymentAttemptId, selectedPayment, onComplete)
        );
    } catch (error) {
        return Promise.reject(error);
    }
};

export const cancelPayment = async (shipmentPaymentAttemptId) => {
    const formData = {
        shipmentPaymentAttemptId
    };

    try {
        await api.cancelPayment(formData);
    } catch (error) {
        return Promise.reject(error);
    }
};

export const completePayment =
    (paymentAttemptId, paymentMethod, onComplete) => async (dispatch) => {
        const formData = {
            paymentAttemptId
        };

        try {
            await api.completePayment(formData);

            //POST successful? then start polling again:
            return dispatch(
                checkPaymentStatus(paymentAttemptId, paymentMethod, onComplete)
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

export const checkPaymentStatus =
    (paymentAttemptId, paymentMethod, onComplete) =>
    async (dispatch, getState) => {
        const { modal } = selectShippingPayment(getState());

        let poll = true;

        try {
            const paymentStatus = await api.getPaymentStatus(paymentAttemptId);

            const { data } = paymentStatus;

            switch (data?.status) {
                case PAYMENT_STATUS.AUTHORIZED:
                case PAYMENT_STATUS.COMPLETING:
                case PAYMENT_STATUS.INIT:
                case PAYMENT_STATUS.CANCELLING:
                    poll = true;
                    break;
                case PAYMENT_STATUS.AWAITING_SWISH:
                    poll = true;
                    if (!modal.open) {
                        dispatch(
                            openPaymentModal({
                                open: MODAL_TYPE.SWISH_HANDEL,
                                status: MODAL_STATUS.START,
                                paymentAttemptId
                            })
                        );
                    }
                    break;
                case PAYMENT_STATUS.INIT_COMPLETE:
                    poll = false;
                    return dispatch(
                        completePayment(
                            paymentAttemptId,
                            paymentMethod,
                            onComplete
                        )
                    );
                case PAYMENT_STATUS.SETTLED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(setCheckoutSummaryFetching(false));
                    onComplete();
                    break;
                case PAYMENT_STATUS.DECLINED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    return Promise.reject(new Error(CHECKOUT_ERRORS.DECLINED));
                case PAYMENT_STATUS.CANCELLED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    break;
                case PAYMENT_STATUS.COMPLETING_FAILED:
                case PAYMENT_STATUS.FAILED:
                case PAYMENT_STATUS.INIT_FAILED:
                case PAYMENT_STATUS.CANCELLING_FAILED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    return Promise.reject(
                        new Error(`Payment error: ${data?.status}`)
                    );
                default:
                    dispatch(closePaymentModal());
                    poll = false;
            }
        } catch (error) {
            poll = false;
            return Promise.reject(error);
        }

        if (poll) {
            await sleep(1500);
            return dispatch(
                checkPaymentStatus(paymentAttemptId, paymentMethod, onComplete)
            );
        }
    };
